import {
  Contract,
  UpdateContractAgentRequest,
  UpdateContractBuyerRequest,
  UpdateContractSellerRequest,
} from '@/types/ContractType';
import { rtkApi } from '../rtkApi';

export const contractApi = rtkApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getContract: builder.query<Contract, string>({
      query: (contractId) => ({ url: `/contract/${contractId}` }),
      providesTags: (_response, _result, contractId) => [
        { type: 'Contract', id: contractId },
      ],
    }),
    getApprovedContract: builder.query<Contract[], void>({
      query: () => ({ url: `/contract/allContracts` }),
      providesTags: (_response, _result) => [{ type: 'Contract' }],
    }),
    getAllContractsForAdmin: builder.query<Contract[], void>({
      query: () => ({ url: `/admin/contracts` }),
      providesTags: (_response, _result) => [{ type: 'Contract' }],
    }),
    getContractByAgent: builder.query<Contract, string>({
      query: (uid) => ({ url: `/contract/agent/${uid}` }),
      providesTags: (_response, _result, uid) => [
        { type: 'Contract Agent', id: uid },
      ],
    }),
    getContractBySeller: builder.query<Contract, string>({
      query: (contractId) => ({ url: `/seller/${contractId}` }),
      providesTags: (_response, _result, contractId) => [
        { type: 'Contract Seller', id: contractId },
      ],
    }),

    // Buyer: list my contracts (used for global dashboard shortcut)
    getMyArchivedContracts: builder.query<ArchivedBuyerContract[], void>({
      query: () => ({ url: `/contract/buyer/archived` }),
      providesTags: [{ type: 'Contract' }],
    }),

    getMyBuyerContracts: builder.query<Contract[], void>({
      query: () => ({ url: `/contract/buyer/my-contracts` }),
      providesTags: (_response, _result) => [{ type: 'Contract' }],
    }),

    updateContractBuyer: builder.mutation<void, UpdateContractBuyerRequest>({
      query: ({ contractId, ...props }) => ({
        url: `/contract/${contractId}/buyer`,
        method: 'PUT',
        data: props,
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'Contract', id: contractId },
      ],
    }),
    updateContractAgent: builder.mutation<void, UpdateContractAgentRequest>({
      query: ({ uid, ...props }) => ({
        url: `/contract/agent/${uid}`,
        method: 'PUT',
        data: props,
      }),
      invalidatesTags: (_result, _error, { uid }) => [
        { type: 'Contract Agent', id: uid },
      ],
    }),
    updateContractSeller: builder.mutation<void, UpdateContractSellerRequest>({
      query: ({ uid, ...props }) => ({
        url: `/contract/seller/${uid}`,
        method: 'PUT',
        data: props,
      }),
      invalidatesTags: (_result, _error, { uid }) => [
        { type: 'Contract Seller', id: uid },
      ],
    }),

    // SIMPLE Priority system
    toggleClausePriority: builder.mutation<
      { success: boolean; priorities: string[] },
      { contractId: string; clauseId: string; isPriority: boolean }
    >({
      query: ({ contractId, clauseId, isPriority }) => ({
        url: `/contract/${contractId}/priority`,
        method: 'POST',
        data: { clauseId, isPriority },
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'Contract', id: contractId },
        'ContractPriorities',
      ],
    }),

    getContractPriorities: builder.query<
      { success: boolean; priorities: string[] },
      string
    >({
      query: (contractId) => ({
        url: `/contract/${contractId}/priorities`,
      }),
      providesTags: ['ContractPriorities'],
    }),

    // Void/reject contract
    voidContract: builder.mutation<
      { success: boolean; message: string; contract: Contract },
      {
        contractId: string;
        voidedReason?: string;
        voidedBy: 'agent' | 'seller' | 'buyer';
      }
    >({
      query: ({ contractId, voidedReason, voidedBy }) => ({
        url: `/contract/${contractId}/void`,
        method: 'POST',
        data: { voidedReason, voidedBy },
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Contract',
      ],
    }),

    // Confirm negotiations complete (agent only)
    confirmNegotiations: builder.mutation<
      { success: boolean; message: string; contract: Contract },
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: `/ratification/${contractId}/confirm-negotiations`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Contract',
      ],
    }),
  }),
});

export type ArchivedBuyerContract = {
  _id: string;
  reason: 'voided' | 'deleted' | 'property_removed';
  contractStatus?: string;
  address: string | null;
  offerPrice: number | null;
  archivedAt?: string;
};

export const {
  useGetMyArchivedContractsQuery,
  useGetContractQuery,
  useGetApprovedContractQuery,
  useGetAllContractsForAdminQuery,
  useGetContractByAgentQuery,
  useGetContractBySellerQuery,
  useGetMyBuyerContractsQuery,

  useUpdateContractBuyerMutation,
  useUpdateContractAgentMutation,
  useUpdateContractSellerMutation,

  useToggleClausePriorityMutation,
  useGetContractPrioritiesQuery,

  useVoidContractMutation,

  useConfirmNegotiationsMutation,
} = contractApi;
